import React, { useState, useRef, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { FaCamera, FaUpload } from "react-icons/fa";
import WorkbookCamera from "./WorkbookCamera";
import * as Sentry from "@sentry/react";
import { UserContext } from "../contexts/UserContext";
import {
  RATE_LIMIT_FALLBACK_MESSAGE,
  extractRateLimitMessageFromResponse,
} from "../utils/rateLimit";

const WORKBOOK_EVALUATE_FILE = "components/WorkbookEvaluate.jsx";

function safeStringify(value, max = 5000) {
  try {
    const json = JSON.stringify(value, (key, val) => {
      if (typeof val === "string" && val.length > 2000) {
        return val.slice(0, 2000) + "…[truncated]";
      }
      return val;
    });
    return json.length > max ? json.slice(0, max) + "…[truncated]" : json;
  } catch {
    return "[Unserializable]";
  }
}

const captureSentryError = (heading, error, extra = {}) => {
  let normalizedError;

  if (error instanceof Error) {
    // Keep original message and stack, trim only if message is huge
    const shortMessage =
      error.message.length > 1000
        ? error.message.slice(0, 1000) + "…[truncated]"
        : error.message;

    normalizedError = new Error(shortMessage);
    normalizedError.name = error.name;
    normalizedError.stack = error.stack;
  } else {
    // Non-Error inputs → wrap into Error
    normalizedError = new Error(
      typeof error === "string" ? error.slice(0, 1000) : heading,
    );
  }

  Sentry.withScope((scope) => {
    scope.setLevel("error");
    scope.setTag("component", "WorkbookEvaluate");
    scope.setExtra("error_heading", heading);
    Object.entries(extra || {}).forEach(([key, value]) => {
      scope.setExtra(key, safeStringify(value));
    });
    if (!(error instanceof Error)) {
      scope.setExtra("original_error_value", error);
    }
    Sentry.captureException(normalizedError);
  });
};

const WorkbookEvaluate = ({
  questionText,
  onEvaluationComplete,
  onFileChange,
  conversationMode,
  playAudio,
  chatHistory,
  currentSubject,
  questionImage,
  onEvaluationStart,
  onEvaluationEnd,
  onEvaluationError,
  isMobile = false,
  onRateLimit,
}) => {
  const { userData } = useContext(UserContext);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showEvaluateTooltip, setShowEvaluateTooltip] = useState(false);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef(null);
  const evaluationSessionRef = useRef(null);
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const triggerRateLimitModal = (message) => {
    if (typeof onRateLimit === "function") {
      onRateLimit(message?.trim() || RATE_LIMIT_FALLBACK_MESSAGE);
    }
  };

  // Notify parent when file status changes
  useEffect(() => {
    if (onFileChange) {
      onFileChange(!!uploadedFile);
    }
  }, [uploadedFile, onFileChange]);

  useEffect(() => {
    if (isMobile && showEvaluateTooltip) {
      setShowEvaluateTooltip(false);
    }
  }, [isMobile, showEvaluateTooltip]);

  const handleEvaluateAnswerClick = () => {
    // Prevent opening modal while an evaluation is in progress
    if (isSubmitting) return;
    setShowUploadOptions(true);
  };

  const handleFileUploadClick = () => {
    fileInputRef.current.click();
    setShowUploadOptions(false);
  };

  // Deactivate Teach me a Topic button
  const isTeachMeDisabled = true; // Set to true to disable the button

  const handleCameraCapture = (file) => {
    setUploadedFile(file);
    const preview = URL.createObjectURL(file);
    setPreviewUrl(preview);
    // Close camera modal after capture
    setShowCamera(false);
    if (onFileChange) {
      onFileChange(true);
    }
    // Notify parent that evaluation starts (show user message + loader)
    try {
      const questionImageUrl = questionImage
        ? URL.createObjectURL(questionImage)
        : null;
      if (onEvaluationStart) {
        evaluationSessionRef.current = onEvaluationStart({
          question: questionText,
          file,
          previewUrl: preview,
          questionImage,
          questionImageUrl,
        });
      }
    } catch {}
    // Auto-submit evaluation immediately after capture using the captured file
    handleSubmitEvaluation(file);
    // Clear local preview and file immediately so UI is clean while waiting
    setUploadedFile(null);
    setPreviewUrl(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      const preview = URL.createObjectURL(file);
      setPreviewUrl(preview);
      setResult(null);
      // Notify parent that evaluation starts (show user message + loader)
      try {
        const questionImageUrl = questionImage
          ? URL.createObjectURL(questionImage)
          : null;
        if (onEvaluationStart) {
          evaluationSessionRef.current = onEvaluationStart({
            question: questionText,
            file,
            previewUrl: preview,
            questionImage,
            questionImageUrl,
          });
        }
      } catch {}
      // Auto-submit evaluation immediately after selecting a file
      handleSubmitEvaluation(file);
      // Clear local preview and file immediately so UI is clean while waiting
      setUploadedFile(null);
      setPreviewUrl(null);
    }
  };

  const handleSubmitEvaluation = async (fileArg) => {
    const fileToUse = fileArg || uploadedFile;
    if (!fileToUse) {
      toast.error("Please upload your answer sheet first.");
      return;
    }
    console.log("Starting evaluation submit...", {
      uploadedFile: fileToUse,
      questionText,
      conversationMode,
      chatHistory,
      currentSubject,
      questionImage,
    });
    // Ensure any modals are closed while submitting
    setShowUploadOptions(false);
    setShowCamera(false);
    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("answerImage", fileToUse);
      formData.append("question", questionText || "");
      formData.append("totalMarks", 5); // Default marks, adjust as needed
      formData.append("conversationMode", conversationMode || false);
      formData.append("chatHistory", JSON.stringify(chatHistory || {}));
      formData.append("currentSubject", currentSubject || "");
      formData.append("board", userData?.board || "");
      formData.append("grades", userData?.grade || "");

      // Add questionImage if available
      if (questionImage) {
        formData.append("questionImage", questionImage);
        console.log(
          "Adding questionImage to FormData:",
          questionImage.name || "questionImage",
        );
      }

      console.log("Sending request to:", `${baseUrl}/evaluate/with-image`);
      const response = await fetch(`${baseUrl}/evaluate/with-image`, {
        method: "POST",
        body: formData,
        timeout: 300000, // 30 seconds timeout
        signal: AbortSignal.timeout(300000), //
      });
      const rateLimitMessage =
        await extractRateLimitMessageFromResponse(response);
      if (rateLimitMessage) {
        toast.warn(rateLimitMessage);
        setIsSubmitting(false);
        triggerRateLimitModal(rateLimitMessage);
        if (onEvaluationError) {
          try {
            onEvaluationError(
              new Error(rateLimitMessage),
              evaluationSessionRef.current,
            );
          } catch {}
        }
        return;
      }
      if (!response.ok) {
        let errMsg = "Evaluation failed";
        try {
          const errData = await response.json();
          if (errData?.error) errMsg = errData.error;
          else if (errData?.detail) errMsg = errData.detail;
        } catch {
          try {
            const txt = await response.text();
            if (txt) errMsg = txt;
          } catch {}
        }
        throw new Error(errMsg);
      }
      const data = await response.json();
      console.log("Evaluation response:", data);

      // Play audio if in conversation mode and audio is available
      if (conversationMode && data?.audio_base64 && playAudio) {
        console.log("Playing audio from evaluation response");
        playAudio(data.audio_base64);
      }

      setResult(null); // Clear local result

      // Create question image URL if available
      const questionImageUrl = questionImage
        ? URL.createObjectURL(questionImage)
        : null;

      console.log("About to call onEvaluationComplete with:", {
        question: questionText,
        file: fileToUse,
        previewUrl,
        questionImage,
        questionImageUrl,
        evaluation: data,
      });
      if (onEvaluationComplete) {
        onEvaluationComplete({
          sessionToken: evaluationSessionRef.current,
          question: questionText,
          file: fileToUse,
          previewUrl,
          questionImage,
          questionImageUrl,
          evaluation: data,
          // Include OCR data from the response if available
          ocrExtractedText: data.ocr_extracted_text,
          ocrSuccess: data.ocr_success,
          ocrConfidence: data.ocr_confidence,
        });
        console.log("onEvaluationComplete callback executed with OCR data");

        // Clear the file after successful evaluation (close selection)
        setUploadedFile(null);
        setPreviewUrl(null);
        setResult(null);
      } else {
        console.warn("onEvaluationComplete callback is not provided");
      }
    } catch (err) {
      captureSentryError(
        `${WORKBOOK_EVALUATE_FILE} | handleSubmitEvaluation | L211`,
        err,
        {
          questionProvided: Boolean(questionText),
          conversationMode,
          currentSubject,
          hasQuestionImage: Boolean(questionImage),
          chatHistoryLength: Array.isArray(chatHistory)
            ? chatHistory.length
            : null,
        },
      );
      console.error("Evaluation error:", err);
      toast.error(err?.message || "Failed to evaluate answer.");
      if (onEvaluationError) {
        try {
          onEvaluationError(err, evaluationSessionRef.current);
        } catch {}
      }
    } finally {
      setIsSubmitting(false);
      if (onEvaluationEnd) {
        try {
          onEvaluationEnd(evaluationSessionRef.current);
        } catch {}
      }
      evaluationSessionRef.current = null;
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setPreviewUrl(null);
    setResult(null);
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
        capture="environment"
      />

      {/* File info section - do not show thumbnail */}
      {uploadedFile && (
        <div className="flex items-center gap-2 text-sm">
          <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center">
            <span className="text-xs text-gray-600">📄</span>
          </div>
          <span className="text-gray-700 font-medium">{uploadedFile.name}</span>
          {!isSubmitting && (
            <button
              onClick={handleRemoveFile}
              className="text-xs text-red-500 hover:underline ml-1"
            >
              (Remove)
            </button>
          )}
        </div>
      )}

      {/* Upload Options Modal */}
      {showUploadOptions && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-opacity duration-200"
          onClick={(e) =>
            e.target === e.currentTarget && setShowUploadOptions(false)
          }
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm overflow-hidden">
            <div className="p-5">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Upload Answer
              </h3>
              <div className="space-y-3">
                <button
                  onClick={() => {
                    setShowUploadOptions(false);
                    setShowCamera(true);
                  }}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-blue-50 text-blue-700 rounded-xl hover:bg-blue-100 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <FaCamera className="text-lg text-blue-600" />
                  </div>
                  <span className="font-medium">Take Photo</span>
                </button>
                <button
                  onClick={handleFileUploadClick}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <FaUpload className="text-lg text-gray-600" />
                  </div>
                  <span className="font-medium">Choose from Files</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      <WorkbookCamera
        isOpen={showCamera}
        onClose={() => setShowCamera(false)}
        onCapture={handleCameraCapture}
      />

      {/* Button section */}
      {!uploadedFile ? (
        <div className="relative">
          {isSubmitting ? (
            <div className="px-4 py-2 bg-indigo-100 text-indigo-700 font-bold rounded-full text-sm select-none">
              Evaluating...
            </div>
          ) : (
            <button
              onClick={handleEvaluateAnswerClick}
              onMouseEnter={
                !isMobile && !isSubmitting
                  ? () => setShowEvaluateTooltip(true)
                  : undefined
              }
              onMouseLeave={
                !isMobile && !isSubmitting
                  ? () => setShowEvaluateTooltip(false)
                  : undefined
              }
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-full transition-colors text-sm flex items-center gap-2"
            >
              Evaluate Answer
            </button>
          )}
          {showEvaluateTooltip && !isMobile && !isSubmitting && (
            <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-gray-600 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-50">
              📝 Select your answer for this question to get evaluation and
              feedback
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <button
            onClick={handleSubmitEvaluation}
            className={`px-4 py-2 text-white font-bold rounded-full transition-colors text-sm ${
              isSubmitting
                ? "bg-indigo-400 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Evaluating..." : "Submit for Evaluation"}
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkbookEvaluate;
